// ============================================================
// TMR Portal do Administrador — root: router + providers + shell
// Montado dentro do gate de auth do App.tsx (usuário já autorizado).
// ============================================================
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import type { TmrSession, AuthorizationScope, ActiveContext, EnvironmentCapabilities } from '@tmr/auth';
import { PermissionsProvider, PERMISSIONS } from './data/PermissionsProvider';
import { PortalShell } from './PortalShell';
import { AccessRequestsPage } from './features/authorization/AccessRequestsPage';
import { AuditLogPage } from './features/authorization/AuditLogPage';
import { RolesPage } from './features/authorization/RolesPage';
import { CompaniesPage } from './features/companies/CompaniesPage';
import { LegacyBackfillPage } from './features/companies/LegacyBackfillPage';
import { CompanyDetail } from './features/companies/CompanyDetail';
import { CompanyApplicationDetailPage } from './features/companyApplications/CompanyApplicationDetailPage';
import { CompanyApplicationsPage } from './features/companyApplications/CompanyApplicationsPage';
import { ContingencyPage } from './features/contingency/ContingencyPage';
import { WildcardCodePage } from './features/contingency/WildcardCodePage';
import { ContractsPage } from './features/contracts/ContractsPage';
import { ContractTemplatesPage } from './features/contractTemplates/ContractTemplatesPage';
import { PatientContractsPage } from './features/patientContracts/PatientContractsPage';
import { ContractTemplateEditorPage } from './features/contractTemplates/ContractTemplateEditorPage';
import { GlobalRatesPage } from './features/globalRates/GlobalRatesPage';
import { BusinessRulesPage } from './features/businessRules/BusinessRulesPage';
import { BusinessRuleDetailPage } from './features/businessRules/BusinessRuleDetailPage';
import { CreditProfilePage } from './features/credit/CreditProfilePage';
import { CreditRulesPage } from './features/credit/CreditRulesPage';
import { ManualReviewDetailPage } from './features/credit/ManualReviewDetailPage';
import { ManualReviewsPage } from './features/credit/ManualReviewsPage';
import { PartiesPage } from './features/parties/PartiesPage';
import { RateSyncPage } from './features/rateSync/RateSyncPage';
import { ProfilePage } from './features/profile/ProfilePage';
import { UsersPage } from './features/users/UsersPage';

export interface PortalProps {
  /** The operator behind the session cookie — present whichever way they signed in (F1). */
  operator: TmrSession;
  /** Permissions resolved by the backend for this session — the portal's only authorization source. */
  permissions: readonly string[];
  /** Empresa/Holding scope — what a grant may reach, and what the context selector will read again. */
  scope: AuthorizationScope | null;
  /** What this deployment is wired for — read by screens that would otherwise infer it from a 404. */
  capabilities: EnvironmentCapabilities;
  /**
   * The operator's persisted active context (10 B6c).
   *
   * **Currently unused — the context selector is hidden** (`09` §11, N5). Kept on the interface, and still
   * resolved by `App.tsx`, because the backend half of B6c is built and tested and nothing about it is
   * being undone: what was missing was a consumer. Wire the selector back in the same commit that gives
   * `activeContext` something to change.
   */
  activeContext: ActiveContext | null;
  onSignOut: () => void | Promise<void>;
}

export function Portal({ operator, permissions, scope, capabilities, onSignOut }: PortalProps) {
  const canManageUsers = permissions.includes(PERMISSIONS.usersManage);
  // Same gate the navigation applies (nav.ts): an operator without it has no link and no route. The
  // server refuses either way — this only keeps the portal from offering a screen that would 403.
  const canAdminAuthorization = permissions.includes(PERMISSIONS.authorizationAdmin);
  const canReadContracts = permissions.includes(PERMISSIONS.contractsRead);
  const canReadParties = permissions.includes(PERMISSIONS.partiesRead);
  const canReadGlobalRates = permissions.includes(PERMISSIONS.ratesGlobalRead);
  const canAdminRates = permissions.includes(PERMISSIONS.companyRatesAdmin);
  const canReadTemplates = permissions.includes(PERMISSIONS.contractTemplatesRead);
  const canReadPatientContracts = permissions.includes(PERMISSIONS.patientContractsRead);
  const canReadBusinessRules = permissions.includes(PERMISSIONS.businessRulesGlobalRead);
  const canReadCredit = permissions.includes(PERMISSIONS.creditReviewsRead);
  const canReadApplications = permissions.includes(PERMISSIONS.companyApplicationsRead);
  const canAccessContingency = permissions.includes(PERMISSIONS.contingencyAccess);

  return (
    <BrowserRouter>
      <PermissionsProvider permissions={permissions} scope={scope} capabilities={capabilities}>
        <Routes>
          <Route
            element={
              // No `contextSelector`: the picker is hidden (`09` §11, N5). It was not inert — choosing an
              // Empresa called `setActiveContext` and moved the session's context on the server — but no
              // query in the portal reads `activeContext`, so it changed server state with nothing on
              // screen reflecting it. The component and its endpoint stay built; only the wiring is gone.
              <PortalShell operator={operator} />
            }
          >
            <Route index element={<Navigate to="/empresas" replace />} />
            <Route path="empresas" element={<CompaniesPage operator={operator} />} />
            {/* Its own route rather than a modal: the walk takes minutes, reports per batch, and has to
                survive the operator looking at something else on the way (19 §9). */}
            <Route path="empresas/importar" element={<LegacyBackfillPage />} />
            {/* A literal segment beside `:companyId`; the router ranks it first whatever the order. */}
            {canReadApplications && <Route path="empresas/solicitacoes" element={<CompanyApplicationsPage />} />}
            {canReadApplications && (
              <Route path="empresas/solicitacoes/:applicationId" element={<CompanyApplicationDetailPage />} />
            )}
            <Route path="empresas/:companyId" element={<CompanyDetail />} />
            {canReadContracts && <Route path="contratos" element={<ContractsPage />} />}
            {canReadPatientContracts && (
              <Route path="contratos-paciente" element={<PatientContractsPage />} />
            )}
            {canReadTemplates && (
              <Route path="modelos-contrato" element={<ContractTemplatesPage />} />
            )}
            {canReadTemplates && (
              <Route path="modelos-contrato/:templateId" element={<ContractTemplateEditorPage />} />
            )}
            {canReadParties && <Route path="partes" element={<PartiesPage />} />}
            {/* The more specific path first: /taxas/sincronizacao is a different screen, gated by a
                different permission, not a tab of the global template. */}
            {canAdminRates && <Route path="taxas/sincronizacao" element={<RateSyncPage />} />}
            {canReadGlobalRates && <Route path="taxas" element={<GlobalRatesPage />} />}
            {canReadBusinessRules && <Route path="regras-negocio" element={<BusinessRulesPage />} />}
            {canReadBusinessRules && (
              <Route path="regras-negocio/:ruleKey" element={<BusinessRuleDetailPage />} />
            )}
            {canReadCredit && <Route path="credito/aprovacoes" element={<ManualReviewsPage />} />}
            {canReadCredit && <Route path="credito/aprovacoes/:evaluationId" element={<ManualReviewDetailPage />} />}
            {canReadCredit && <Route path="credito/consulta" element={<CreditProfilePage />} />}
            {canReadBusinessRules && <Route path="credito/regras" element={<CreditRulesPage />} />}
            {canAccessContingency && <Route path="contingencia" element={<ContingencyPage />} />}
            {canAccessContingency && (
              <Route path="contingencia/codigo-coringa" element={<WildcardCodePage operatorId={operator.userId} />} />
            )}
            <Route path="perfil" element={<ProfilePage operator={operator} onSignOut={onSignOut} />} />
            {canManageUsers && <Route path="usuarios" element={<UsersPage />} />}
            {canAdminAuthorization && <Route path="papeis" element={<RolesPage />} />}
            {canAdminAuthorization && <Route path="acessos" element={<AccessRequestsPage />} />}
            {canAdminAuthorization && <Route path="auditoria" element={<AuditLogPage />} />}
            <Route path="*" element={<Navigate to="/empresas" replace />} />
          </Route>
        </Routes>
      </PermissionsProvider>
    </BrowserRouter>
  );
}
